import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';
import { getApiErrorMessage } from '../../lib/apiErrors';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  RECORDING_VISIBILITY_OPTIONS,
  SCORES_ALWAYS_VISIBLE_NOTE,
  widensRecordingAccess,
} from '../../lib/recordingVisibility';
import type { RecordingVisibility, SessionViewer } from './types';

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
}

type Props = {
  sessionId: string;
  currentUserId: string | undefined;
  recordingVisibility: RecordingVisibility;
  viewers: SessionViewer[];
  onSaved: (level: RecordingVisibility, viewers: SessionViewer[]) => void;
};

export function RecordingAccessControl({
  sessionId,
  currentUserId,
  recordingVisibility,
  viewers,
  onSaved,
}: Props) {
  const [level, setLevel] = useState<RecordingVisibility>(recordingVisibility);
  const [selected, setSelected] = useState<string[]>(() => viewers.map((v) => v.userId));
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setLevel(recordingVisibility);
    setSelected(viewers.map((v) => v.userId));
  }, [recordingVisibility, viewers]);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<OrgMember[]>('/orgs/me/members')
      .then((res) => {
        if (!cancelled) setMembers(res.data.filter((m) => m.id !== currentUserId));
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const savedViewerIds = useMemo(() => viewers.map((v) => v.userId), [viewers]);
  const effectiveSelected = level === 'named_viewers' ? selected : [];
  const savedEffective = recordingVisibility === 'named_viewers' ? savedViewerIds : [];
  const dirty =
    level !== recordingVisibility ||
    effectiveSelected.length !== savedEffective.length ||
    effectiveSelected.some((id) => !savedEffective.includes(id));
  const widens = widensRecordingAccess(
    { level: recordingVisibility, viewerIds: savedEffective },
    { level, viewerIds: effectiveSelected },
  );

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/practice/sessions/${sessionId}/recording-access`, {
        recordingVisibility: level,
        viewerUserIds: effectiveSelected,
      });
      const nextViewers: SessionViewer[] = effectiveSelected.map((id) => {
        const m = members.find((x) => x.id === id);
        const prev = viewers.find((v) => v.userId === id);
        return {
          userId: id,
          userName: m?.name ?? prev?.userName ?? null,
          userEmail: m?.email ?? prev?.userEmail ?? '',
        };
      });
      onSaved(level, nextViewers);
      setConfirmOpen(false);
    } catch (err) {
      Sentry.captureException(err);
      setError(getApiErrorMessage(err));
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const help = RECORDING_VISIBILITY_OPTIONS.find((o) => o.value === level)?.help;

  return (
    <div className="mb-3 rounded-lg border border-gray-200 p-4" data-testid="recording-access">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <label htmlFor="recording-visibility" className="text-sm font-medium text-gray-900">
          Who can review this session
        </label>
        <select
          id="recording-visibility"
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={level}
          disabled={saving}
          onChange={(e) => setLevel(e.target.value as RecordingVisibility)}
        >
          {RECORDING_VISIBILITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => (widens ? setConfirmOpen(true) : void save())}
          disabled={!dirty || saving}
          className="sm:ml-auto bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {help && <p className="text-xs text-gray-500 mt-2">{help}</p>}
      <p className="text-xs text-gray-500 mt-1">{SCORES_ALWAYS_VISIBLE_NOTE}</p>

      {level === 'named_viewers' && (
        <fieldset className="mt-3">
          <legend className="text-sm text-gray-700 mb-2">Teammates who can review</legend>
          {members.length === 0 ? (
            <p className="text-sm text-gray-500">No other members in this organization.</p>
          ) : (
            <ul className="space-y-2 max-h-40 overflow-y-auto">
              {members.map((m) => (
                <li key={m.id}>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                      checked={selected.includes(m.id)}
                      onChange={() => toggle(m.id)}
                    />
                    <span>
                      <span className="text-gray-900">{m.name || m.email}</span>
                      {m.name && <span className="block text-gray-500 text-xs">{m.email}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
      )}

      {error && (
        <p className="text-xs text-red-600 mt-2" role="alert">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Share this session?"
        description={
          level === 'rep_and_managers'
            ? 'Owners, admins, and managers will be able to read the full transcript and written feedback for this session.'
            : 'The teammates you selected will be able to read the full transcript and written feedback for this session.'
        }
        confirmLabel="Share"
        variant="default"
        loading={saving}
        onConfirm={save}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
